import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/auth";
import { MessageCircle, Mail, CheckCircle2, KeyRound, XCircle, Loader2, Hourglass } from "lucide-react";

export const ORDER_STATUS = {
  pending: ["En attente", "text-zinc-300 border-zinc-600"],
  processing: ["En cours de traitement", "text-sky-400 border-sky-500/40"],
  payment_confirmed: ["Paiement confirmé", "text-amber-400 border-amber-500/40"],
  activated: ["Activée", "text-emerald-400 border-emerald-500/40"],
  rejected: ["Refusée", "text-red-400 border-red-500/40"],
  expired: ["Expirée", "text-zinc-500 border-zinc-700"],
};
const PLANS = ["1M", "3M", "6M", "12M"];
const METHODS = [["", "Moyen de paiement"], ["airtel", "Airtel Money"], ["orange", "Orange Money"], ["binance", "Binance Pay"], ["visa", "Visa / Carte"]];

export function OrderStatusPill({ s }) {
  const [l, c] = ORDER_STATUS[s] || [s, "text-zinc-400 border-zinc-700"];
  return <span data-testid={`order-status-${s}`} className={`text-[10px] px-2 py-0.5 rounded-full border font-mono-tech uppercase ${c}`}>{l}</span>;
}

export function LicenseOrdersAdmin() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => axios.get(`${API}/admin/license-orders`, { params: filter ? { status: filter } : {} }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="card-lhs p-5 space-y-4" data-testid="license-orders-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-bold uppercase tracking-wider text-sm">Commandes de licences</h2>
        <div className="flex flex-wrap gap-1">
          {[["", "Toutes"], ...Object.entries(ORDER_STATUS).map(([k, v]) => [k, v[0]])].map(([k, l]) => (
            <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`orders-filter-${k || "all"}`}
              className={`px-2.5 py-1 rounded text-[10px] uppercase font-mono-tech border transition-colors ${filter === k ? "bg-orange-500 text-black border-orange-500" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto scroll-thin">
        {rows.length === 0 && <div className="text-sm text-zinc-500 italic">Aucune commande.</div>}
        {rows.map(o => <OrderRow key={o.id} o={o} onChange={load} />)}
      </div>
    </div>
  );
}

function OrderRow({ o, onChange }) {
  const [price, setPrice] = useState(o.agreed_price ?? "");
  const [method, setMethod] = useState(o.payment_method || "");
  const [plan, setPlan] = useState(o.plan || "3M");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const closed = ["activated", "rejected", "expired"].includes(o.status);

  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); onChange(); } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  const patch = (extra) => run(() => axios.patch(`${API}/admin/license-orders/${o.id}`, {
    agreed_price: price === "" ? null : Number(price), payment_method: method || null, ...extra,
  }));
  const activate = () => run(async () => {
    await axios.patch(`${API}/admin/license-orders/${o.id}`, { agreed_price: price === "" ? null : Number(price), payment_method: method || null });
    await axios.post(`${API}/admin/license-orders/${o.id}/activate`, { plan });
  });
  const msg = encodeURIComponent(`Bonjour ${o.username}, concernant votre commande ${o.reference} (${o.software})`);

  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0C0C10] p-4 text-xs space-y-3" data-testid={`order-row-${o.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-tech text-orange-400 text-sm">{o.reference}</span>
            <OrderStatusPill s={o.status} />
            <span className="text-zinc-300">{o.software}</span>
          </div>
          <div className="text-zinc-400 mt-1">{o.username} · {o.email} {o.user_id ? "" : <span className="text-amber-400">· sans compte</span>}</div>
          {o.message && <div className="text-zinc-500 mt-1 italic">« {o.message} »</div>}
          {o.expires_at && <div className="text-zinc-500 mt-1">{o.plan} · expire le {new Date(o.expires_at).toLocaleDateString("fr-FR")}{o.license_key ? ` · ${o.license_key}` : ""}</div>}
        </div>
        <div className="flex gap-2">
          <a href={`mailto:${o.email}?subject=${encodeURIComponent("Commande " + o.reference)}&body=${msg}`} data-testid={`order-contact-${o.id}`}
            className="px-2.5 py-1.5 rounded-lg border border-zinc-700 hover:border-orange-500 inline-flex items-center gap-1 transition-colors"><Mail className="w-3.5 h-3.5" /> Contacter le client</a>
        </div>
      </div>
      {!closed && (
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid={`order-price-${o.id}`} type="number" min={0} step="any" placeholder="Prix convenu" value={price} onChange={e => setPrice(e.target.value)} className="input-lhs !w-32 !py-1.5" />
          <select data-testid={`order-method-${o.id}`} value={method} onChange={e => setMethod(e.target.value)} className="input-lhs !w-40 !py-1.5">
            {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid={`order-plan-${o.id}`} value={plan} onChange={e => setPlan(e.target.value)} className="input-lhs !w-24 !py-1.5">
            {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <Btn testid={`order-processing-${o.id}`} onClick={() => patch({ status: "processing" })} icon={Hourglass}>En cours</Btn>
          <Btn testid={`order-confirm-${o.id}`} onClick={() => patch({ status: "payment_confirmed" })} icon={CheckCircle2}>Confirmer le paiement</Btn>
          <Btn testid={`order-activate-${o.id}`} onClick={activate} icon={KeyRound} primary>Activer la licence</Btn>
          <Btn testid={`order-reject-${o.id}`} onClick={() => window.confirm("Refuser cette commande ?") && patch({ status: "rejected" })} icon={XCircle} danger>Refuser</Btn>
          {busy && <Loader2 className="w-4 h-4 animate-spin text-orange-400" />}
        </div>
      )}
      {o.status === "activated" && <div className="text-emerald-400"><MessageCircle className="w-3.5 h-3.5 inline mr-1" />{o.email_sent ? "E-mail de confirmation envoyé" : "E-mail non envoyé"}</div>}
      {err && <div className="text-red-400" data-testid={`order-err-${o.id}`}>{err}</div>}
    </div>
  );
}

function Btn({ children, onClick, icon: Icon, primary, danger, testid }) {
  const cls = primary ? "btn-orange" : danger ? "border border-red-500/40 text-red-400 hover:bg-red-500/10" : "border border-zinc-700 hover:border-orange-500";
  return (
    <button type="button" data-testid={testid} onClick={onClick} className={`px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1 transition-colors ${cls}`}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}
